import { useRef, useEffect, useState } from "react";
import type { Tool, User } from "@/lib/types";
import { CATEGORY_LABELS } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin } from "lucide-react";

interface MapViewProps {
	tools: (Tool & { owner?: User })[];
	onSelectTool: (toolId: string) => void;
	selectedToolId?: string;
}

const CATEGORY_COLORS: Record<string, string> = {
	"power-tools": "#ef4444",
	"hand-tools": "#f97316",
	garden: "#22c55e",
	automotive: "#3b82f6",
	cleaning: "#8b5cf6",
	painting: "#ec4899",
	plumbing: "#06b6d4",
	electrical: "#eab308",
	moving: "#64748b",
	other: "#a3a3a3",
};

const CENTER_LAT = 40.73;
const CENTER_LNG = -73.99;
const MAP_WIDTH = 800;
const MAP_HEIGHT = 500;

function latLngToPixel(
	lat: number,
	lng: number,
	bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number }
) {
	const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * MAP_WIDTH;
	const y =
		((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * MAP_HEIGHT;
	return { x, y };
}

export function MapView({ tools, onSelectTool, selectedToolId }: MapViewProps) {
	const [hoveredTool, setHoveredTool] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const bounds = {
		minLat: CENTER_LAT - 0.025,
		maxLat: CENTER_LAT + 0.025,
		minLng: CENTER_LNG - 0.035,
		maxLng: CENTER_LNG + 0.035,
	};

	// Generate street grid lines
	const gridLines = [];
	for (let i = 0; i <= 8; i++) {
		const y = (i / 8) * MAP_HEIGHT;
		gridLines.push(
			<line
				key={`h-${i}`}
				x1={0}
				y1={y}
				x2={MAP_WIDTH}
				y2={y}
				stroke="currentColor"
				strokeWidth={i % 4 === 0 ? 1 : 0.5}
				className="text-border"
				opacity={i % 4 === 0 ? 0.6 : 0.3}
			/>
		);
	}
	for (let i = 0; i <= 10; i++) {
		const x = (i / 10) * MAP_WIDTH;
		gridLines.push(
			<line
				key={`v-${i}`}
				x1={x}
				y1={0}
				x2={x}
				y2={MAP_HEIGHT}
				stroke="currentColor"
				strokeWidth={i % 5 === 0 ? 1 : 0.5}
				className="text-border"
				opacity={i % 5 === 0 ? 0.6 : 0.3}
			/>
		);
	}

	// Generate park areas
	const parks = [
		{ x: 120, y: 80, w: 100, h: 70 },
		{ x: 500, y: 300, w: 130, h: 90 },
		{ x: 650, y: 100, w: 80, h: 60 },
	];

	const activeTool =
		tools.find((t) => t.id === (hoveredTool || selectedToolId)) || null;

	return (
		<div className="relative w-full" ref={containerRef}>
			<svg
				viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
				className="w-full h-auto rounded-lg border border-border bg-muted/30"
				style={{ minHeight: 300, maxHeight: 500 }}
			>
				{/* Background */}
				<rect width={MAP_WIDTH} height={MAP_HEIGHT} className="fill-background" />

				{/* Parks */}
				{parks.map((park, i) => (
					<rect
						key={`park-${i}`}
						x={park.x}
						y={park.y}
						width={park.w}
						height={park.h}
						rx={8}
						fill="hsl(142 40% 92%)"
						stroke="hsl(142 40% 80%)"
						strokeWidth={1}
					/>
				))}

				{/* Street grid */}
				{gridLines}

				{/* Street labels */}
				<text x={15} y={MAP_HEIGHT / 4 + 4} className="fill-muted-foreground text-[10px]" opacity={0.5}>
					Maple Ave
				</text>
				<text x={15} y={MAP_HEIGHT / 2 + 4} className="fill-muted-foreground text-[10px]" opacity={0.5}>
					Cedar St
				</text>
				<text x={15} y={(MAP_HEIGHT * 3) / 4 + 4} className="fill-muted-foreground text-[10px]" opacity={0.5}>
					Oak Blvd
				</text>

				{/* Tool markers */}
				{tools.map((tool) => {
					const pos = latLngToPixel(
						tool.location.lat,
						tool.location.lng,
						bounds
					);
					const isActive = tool.id === hoveredTool || tool.id === selectedToolId;
					const color = CATEGORY_COLORS[tool.category] || "#a3a3a3";

					return (
						<g
							key={tool.id}
							onClick={() => onSelectTool(tool.id)}
							onMouseEnter={() => setHoveredTool(tool.id)}
							onMouseLeave={() => setHoveredTool(null)}
							className="cursor-pointer"
						>
							{isActive && (
								<circle
									cx={pos.x}
									cy={pos.y}
									r={18}
									fill={color}
									opacity={0.15}
								>
									<animate
										attributeName="r"
										values="15;20;15"
										dur="2s"
										repeatCount="indefinite"
									/>
								</circle>
							)}
							<circle
								cx={pos.x}
								cy={pos.y}
								r={isActive ? 9 : 7}
								fill={color}
								stroke="white"
								strokeWidth={2}
								style={{
									transition: "r 0.15s ease",
									filter: isActive
										? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
										: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
								}}
							/>
							{isActive && (
								<text
									x={pos.x}
									y={pos.y - 16}
									textAnchor="middle"
									className="fill-foreground text-[11px] font-medium"
									style={{
										filter: "drop-shadow(0 1px 2px rgba(255,255,255,0.8))",
									}}
								>
									${tool.dailyRate}/day
								</text>
							)}
						</g>
					);
				})}

				{/* Neighborhood labels */}
				<text
					x={MAP_WIDTH / 4}
					y={30}
					textAnchor="middle"
					className="fill-muted-foreground text-xs font-medium"
					opacity={0.4}
				>
					Maplewood Heights
				</text>
				<text
					x={(MAP_WIDTH * 3) / 4}
					y={30}
					textAnchor="middle"
					className="fill-muted-foreground text-xs font-medium"
					opacity={0.4}
				>
					Cedar Park
				</text>
				<text
					x={MAP_WIDTH / 2}
					y={MAP_HEIGHT - 15}
					textAnchor="middle"
					className="fill-muted-foreground text-xs font-medium"
					opacity={0.4}
				>
					Riverside Commons
				</text>
			</svg>

			{/* Map legend */}
			<div className="absolute bottom-3 left-3 bg-background/90 backdrop-blur-sm rounded-md border border-border p-2">
				<div className="flex flex-wrap gap-2">
					{Object.entries(CATEGORY_COLORS)
						.slice(0, 6)
						.map(([cat, color]) => (
							<div key={cat} className="flex items-center gap-1">
								<div
									className="w-2.5 h-2.5 rounded-full"
									style={{ backgroundColor: color }}
								/>
								<span className="text-[10px] text-muted-foreground">
									{CATEGORY_LABELS[cat as keyof typeof CATEGORY_LABELS]}
								</span>
							</div>
						))}
				</div>
			</div>

			{/* Hover tooltip */}
			{activeTool && (
				<div className="absolute top-3 right-3 bg-background border border-border rounded-lg p-3 shadow-lg max-w-[220px]">
					<p className="font-medium text-sm leading-tight">{activeTool.name}</p>
					<div className="flex items-center gap-2 mt-1.5">
						<Badge variant="secondary" className="text-[10px] px-1.5 py-0">
							{CATEGORY_LABELS[activeTool.category]}
						</Badge>
						<span className="text-xs text-muted-foreground flex items-center gap-0.5">
							<Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
							{activeTool.rating}
						</span>
					</div>
					<div className="flex items-center justify-between mt-2">
						<span className="text-sm font-semibold text-primary">
							${activeTool.dailyRate}/day
						</span>
						<span className="text-xs text-muted-foreground flex items-center gap-0.5">
							<MapPin className="w-3 h-3" />
							{activeTool.neighborhood}
						</span>
					</div>
				</div>
			)}
		</div>
	);
}
